<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="(course.id ? 'Edit: ' + course.course_name : 'Create') + ' [Training Courses] :: MeritEHS'"/>

            <h4 class="py-2 mb-3">
                <ol class="breadcrumb font-weight-bold m-0 d-block d-md-inline-block">
                    <li class="breadcrumb-item font-weight-light d-block d-md-inline-block">
                        <router-link :to="`/training-courses`">
                            <i class="fa fa-graduation-cap"></i> Training Courses
                        </router-link>
                    </li>
                    <template v-if="course.id">
                        <li class="breadcrumb-item font-weight-light d-block d-md-inline-block mt-md-0 mt-2">
                            <router-link :to="`/training-courses/view/${course.id}`">
                                {{ course.course_name }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active d-block d-md-inline-block mt-md-0 mt-2">New Training Course</li>
                    </template>
                    <li class="ml-3 d-sm-inline-block d-none">
                        <button type="button" class="btn btn-sm btn-success" v-on:click="saveTrainingCourse(this)" :disabled="saving">
                            <span v-if="course.id">Update</span>
                            <span v-else>Create</span>
                            Training Course
                        </button>
                    </li>
                </ol>
            </h4>

            <div class="d-sm-none d-block mb-3">
                <button type="button" class="btn btn-sm btn-success" v-on:click="saveTrainingCourse(this)" :disabled="saving">
                    <span v-if="course.id">Update</span>
                    <span v-else>Create</span>
                    Training Course
                </button>
            </div>

            <div class="row">
                <div class="col-lg-8 col-12">
                    <div class="card mb-3">
                        <div class="card-header">Training Course</div>
                        <div class="card-body">

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.course_name }" class="row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.course_name}">
                                    Course Name:
                                    <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-input v-model="course.course_name" :class="{'is-invalid': errors.course_name}" placeholder="Enter a Training Course Name"></b-form-input>

                                    <div v-if="errors.course_name" class="text-danger">
                                        <span v-for="error in errors.course_name">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.valid_days }" class="row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.valid_days}">
                                    Valid for Days:
                                    <i class="fa fa-question-circle" title="This is the default number of days from the completed date that the course expires"></i>
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-input type="number" v-model="course.valid_days" :class="{'is-invalid': errors.valid_days}" placeholder="Enter Number of Days this Course is Valid after Completion"></b-form-input>

                                    <div v-if="errors.valid_days" class="text-danger">
                                        <span v-for="error in errors.valid_days">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.enable_public_access}">
                                    Enable Public Access:
                                    <i class="fa fa-question-circle" title="If enabled, MeritEHS will generate a unique URL and QR Code to access this training course content. Employee# is required to access unless Anonymous access is enabled."></i>
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-checkbox v-model="course.enable_public_access" :value="1" :unchecked-value="0" plain></b-form-checkbox>

                                    <div v-if="errors.enable_public_access" class="text-danger">
                                        <span v-for="error in errors.enable_public_access">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="row" v-if="course.enable_public_access == 1">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.enable_anonymous_access}">
                                    Enable Anonymous Access:
                                    <i class="fa fa-question-circle" title="If enabled, Employee# verification will not be required to access and complete the training course content."></i>
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-checkbox v-model="course.enable_anonymous_access" :value="1" :unchecked-value="0" plain></b-form-checkbox>

                                    <div v-if="errors.enable_anonymous_access" class="text-danger">
                                        <span v-for="error in errors.enable_anonymous_access">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <template v-if="course.enable_public_access == 1">
                                <div class="row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.require_signature}">
                                        Require Signature
                                        <i class="fa fa-question-circle" title="This will require the employee or guest to sign to complete the course."></i>
                                    </label>
                                    <div class="col-md-8 col-sm-12">
                                        <b-form-checkbox v-model="course.require_signature" :value="1" :unchecked-value="0" plain></b-form-checkbox>

                                        <div v-if="errors.require_signature" class="text-danger">
                                            <span v-for="error in errors.require_signature">{{ error }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="row">
                                    <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.require_jobsite}">
                                        Require Jobsite
                                        <i class="fa fa-question-circle" title="This will require the employee or guest to select a Jobsite from your list of jobsites."></i>
                                    </label>
                                    <div class="col-md-8 col-sm-12">
                                        <b-form-checkbox v-model="course.require_jobsite" :value="1" :unchecked-value="0" plain></b-form-checkbox>

                                        <div v-if="errors.require_jobsite" class="text-danger">
                                            <span v-for="error in errors.require_jobsite">{{ error }}</span>
                                        </div>
                                    </div>
                                </div>
                            </template>

                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Certificate Template:</strong>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-info">
                                <p>Upload a Word Document Mail Merge Template to enable Certificate Auto-Creation. <strong>Note:</strong> Template Tags must be formatted like so: <strong>${field_name_here}</strong></p>
                                <p>Download the <a href="/documents/MeritEHS-Example-Certificate.docx">Example Mail Merge Template</a> to get started</p>
                                <div>
                                    Available Merge Fields:
                                    <ul>
                                        <li v-for="field in mergable_fields">${<span>{{ field }}</span>}</li>
                                    </ul>
                                </div>
                            </div>

                            <div class="row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.certificate_template_id}">
                                    Certificate Template:
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-file v-model="certificate_template" placeholder="Template must be a Docx or Doc file type" accept=".docx, .doc" />

                                    <div v-if="course.certificate_template_id" class="mt-1 mb-1 text-muted">
                                        Existing Certificate Template: <a :href="`/files/download/${course.certificate_template_id}`">{{ course.certificate_template_name }}</a>
                                    </div>

                                    <div v-if="course.certificate_template_id" class="mt-1 mb-1 text-muted">
                                        <a href="javascript:void(0)" v-on:click="deleteCourseFile('certificate_template_id')" :disabled="deleting_certificate_template_id" class="text-danger">
                                            <i class="fa fa-trash"></i> Delete Certificate Template <i class="fa fa-spin fa-spinner" v-if="deleting_certificate_template_id"></i>
                                        </a>
                                    </div>

                                    <div v-if="errors.certificate_template_id" class="text-danger">
                                        <span v-for="error in errors.certificate_template_id">{{ error }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong>Course Details (Include Dates, Instructors, Talking Points, or any other notes):</strong>
                        </div>
                        <div class="card-body">
                            <vue-editor v-model="course.course_details" placeholder="Enter Details about the Training Course" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                        </div>
                    </div>

                    <div class="card mb-3" v-if="course.enable_public_access == 1">
                        <div class="card-header">
                            <strong>Course Video:</strong>
                        </div>
                        <div class="card-body">
                            <div class="alert alert-info">
                                IF a Course Video is uploaded here, the visitor completed the training course will be forced to watch the entire video before he/she can certify course completed with their signature.
                            </div>

                            <div class="row">
                                <label :class="{ 'col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.course_video_id}">
                                    Course Video:
                                </label>
                                <div class="col-md-8 col-sm-12">
                                    <b-form-file v-model="course_video" placeholder="Video must be mp4, ogg, or webm format" accept=".mp4, .ogv, .webm" />

                                    <div v-if="course.course_video_id" class="mt-1 mb-1 text-muted">
                                        Existing Course Video: <a :href="`/files/download/${course.course_video_id}`">{{ course.video_name }}</a>
                                    </div>

                                    <div v-if="course.course_video_id" class="mt-1 mb-1 text-muted">
                                        <a href="javascript:void(0)" v-on:click="deleteCourseFile('course_video_id')" :disabled="deleting_course_video_id" class="text-danger">
                                            <i class="fa fa-trash"></i> Delete Course Video <i class="fa fa-spin fa-spinner" v-if="deleting_course_video_id"></i>
                                        </a>
                                    </div>

                                    <div v-if="errors.course_video_id" class="text-danger">
                                        <span v-for="error in errors.course_video_id">{{ error }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card mb-3" v-if="course.enable_public_access == 1">
                        <div class="card-header">
                            <strong>Completed Message <small>This will be displayed to the employee/guest upon completing the Training Course.</small></strong>
                        </div>
                        <div class="card-body">
                            <vue-editor v-model="course.completed_msg" placeholder="(Optional) Enter a Message for the Employee or Guest upon completing the Training Course" :editor-toolbar="this.$root.customVueEditorToolbar"></vue-editor>
                        </div>
                    </div>

                    <div class="card mb-3">
                        <div class="card-header">
                            <strong><i class="fa fa-files-o"></i> Files &amp; Photos</strong>
                        </div>
                        <div class="card-body">
                            <template v-if="course.id">
                                <file-uploader assetType="TrainingCourse" :assetId="course.id" />
                                <hr/>

                                <file-list :files="files" />
                            </template>
                            <template v-else>
                                <div class="alert alert-info">
                                    In order to better process file uploads, you must first save the Training Course before uploading files
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="col-lg-4 col-12">
                    <div class="card mb-3" v-if="catalogs.length > 0">
                        <div class="card-header">Catalogs</div>
                        <div class="card-body">
                            <b-form-checkbox-group v-model="course_catalogs" :options="catalog_options" plain></b-form-checkbox-group>
                        </div>
                    </div>
                </div>
            </div>
            <hr/>
            <button type="button" class="btn btn-success" v-on:click="saveTrainingCourse(this)" :disabled="saving">
                <i class="fa fa-spin fa-spinner" v-if="saving"></i>
                <span v-if="course.id">Update</span>
                <span v-else>Create</span>
                Training Course
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                deleting_certificate_template_id: false,
                deleting_course_video_id: false,
                certificate_template: null,
                course_video: null,
                id: this.$route.params.id,
                course: {},
                files: [],
                errors: {},
                course_catalogs: [],
                catalogs: [],
                mergable_fields: [],
            }
        },

        computed: {
            catalog_options(){
                //Setup
                var options = [];

                for (var i = 0; i < this.catalogs.length; i++)
                {
                    options.push({text: this.catalogs[i].catalog_name, value: this.catalogs[i].id});
                }

                //Send it
                return options;
            },
        },

        methods: {

            getTrainingCourse() {
                //Get the data
                this.$http
                    .get('/training-courses/edit' + (this.id ? '/' + this.id : ''))
                    .then(response => {
                        //Set all the data
                        this.course = response.data.course;
                        this.files = response.data.files;
                        this.mergable_fields = response.data.mergable_fields;

                        //Set the catalog IDs
                        this.catalogs = response.data.catalogs;
                        this.course_catalogs = response.data.course_catalogs;
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveTrainingCourse() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Setup
                this.course.course_catalogs = this.course_catalogs;

                //Send it
                this.$http
                //.postWithSingleFile('/training-courses/edit', this.course, this.certificate_template, 'certificate_template')
                .postWithFiles('/training-courses/edit', this.course, {certificate_template: this.certificate_template, course_video: this.course_video})
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('Training Course Saved');

                    //Return to main list?
                    if(this.course.id)
                    {
                        //Send it
                        this.$router.push('/training-courses/view/' + this.course.id);
                    }
                    else
                    {
                        //reload to edi
                        this.$router.push('/training-courses/edit/' + response.data.id);
                    }

                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            deleteCourseFile(file_attr)
            {
                //Send it
                this['deleting_'+file_attr] = true;

                //Send it
                this.$http
                .post('/training-courses/delete-file/'+this.course.id, {
                    file_id: this.course[file_attr],
                    attribute: file_attr,
                })
                .then(response => {
                    //Reset it
                    this.course[file_attr] = null;

                    //Alert the user?
                    this.$toastr.success('File Deleted');
                })
                .catch(error => {

                })
                .then(response => {
                    //REset
                    this['deleting_'+file_attr] = true;
                });
            }
        },

        mounted() {
            //Get the Training Course
            this.getTrainingCourse();
        }
    }
</script>
